import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TableContainer,
  Typography,
  CircularProgress,
  TablePagination,
  IconButton,
  Box,
  Alert,
  Snackbar,
  TextField,
  Autocomplete,
  InputAdornment,
  Button,
  MenuItem,
  Checkbox,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import ClearIcon from '@mui/icons-material/Clear';
import CashOutForm from './CashOutForm';
import DialogButton from './../commons/DialogButton';
import EditableRow from './EditableRow';
import SuggestLinksDialog from '../budget/SuggestLinksDialog';
import { formatValue, toDisplayDate } from '../../utils/utils';
import walletApi, { getRecurringExpenses } from '../../api/wallet';
import { getErrorMessage } from '../../utils/errors';
import useSorting from '../../utils/useSorting';

const columns = [
  { id: 'select', label: '', sortable: false, width: 50 },
  { id: 'Description', label: 'Descricao', sortable: true },
  { id: 'Category.Name', label: 'Categoria', sortable: true },
  { id: 'Currency', label: 'Moeda', sortable: true, hideOnMobile: true },
  { id: 'Value', label: 'Valor', sortable: true },
  { id: 'DebitDate', label: 'Data', sortable: true, hideOnSm: true },
  { id: 'Observation', label: 'Obs', sortable: false, hideOnLg: true },
  { id: 'IsMandatory', label: 'Fixo', sortable: true },
  { id: 'actions', label: '', sortable: false },
];

const CashOutList = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [categories, setCategories] = useState([]);
  const [recurringItems, setRecurringItems] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [currencies] = useState(['USD', 'BRL']);
  const [errorOpen, setErrorOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [filters, setFilters] = useState({
    category: null,
    currency: null,
    mandatory: '',
    startDate: '',
    endDate: '',
    description: '',
    showCorrelated: false,
  });

  const valueTransformers = {
    Value: (item) => Number(item.Value) || 0,
    'Category.Name': (item) => item.Category?.Name || '',
    IsMandatory: (item) => (item.IsMandatory ? 1 : 0),
    DebitDate: (item) => {
      if (!item.DebitDate) return 0;
      const [day, month, year] = item.DebitDate.split('/');
      return new Date(year, month - 1, day).getTime();
    },
  };

  const { orderBy, order, sortedData, handleRequestSort } = useSorting(
    data,
    'DebitDate',
    'desc',
    valueTransformers
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });

      if (filters.category) queryParams.append('category', filters.category.ID);
      if (filters.currency) queryParams.append('currency', filters.currency);
      if (filters.mandatory !== '') queryParams.append('mandatory', filters.mandatory);
      if (filters.startDate) queryParams.append('start_date', filters.startDate);
      if (filters.endDate) queryParams.append('end_date', filters.endDate);
      if (filters.description.length >= 3) queryParams.append('description', filters.description);
      // Filter correlated (child) expenses based on toggle
      if (!filters.showCorrelated) {
        queryParams.append('is_correlated', 'false');
      }

      const [cashOutResponse, categoryResponse] = await Promise.all([
        walletApi.get(`/cashout?${queryParams.toString()}`),
        walletApi.get(`/category?category_type=cash_out`),
      ]);

      setCategories(categoryResponse.data.Categories || []);

      if (cashOutResponse.status === 200) {
        setData(cashOutResponse.data.Items || []);
        setTotalItems(cashOutResponse.data.TotalItems || 0);
      } else if (cashOutResponse.status === 204) {
        setData([]);
        setTotalItems(0);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Nao foi possivel carregar os dados.'));
      setErrorOpen(true);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    if (filters.description.length >= 3 || filters.description === '') {
      fetchData();
    }
  }, [fetchData, filters.description]);

  useEffect(() => {
    getRecurringExpenses()
      .then((res) => setRecurringItems(res.data?.Items || []))
      .catch(() => setRecurringItems([]));
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    if (field !== 'description') {
      setPage(0);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({
      category: null,
      currency: null,
      mandatory: '',
      startDate: '',
      endDate: '',
      description: '',
      showCorrelated: false,
    });
    setPage(0);
  };

  const handleDelete = async (id) => {
    try {
      await walletApi.delete(`/cashout/${id}`);
      setData((prevData) => prevData.filter((item) => item.ID !== id));
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao excluir o lancamento.'));
      setErrorOpen(true);
    }
  };

  const handleUpdate = async (id, description, categoryId, observation, isMandatory, value, debitDate) => {
    try {
      const payload = {
        id,
        description,
        category_id: categoryId,
        observation,
        is_mandatory: isMandatory,
        value: String(value),
        debit_date: toDisplayDate(debitDate),
      };
      await walletApi.put('/cashout', payload);
      setData((prevData) =>
        prevData.map((item) =>
          item.ID === id
            ? {
                ...item,
                Description: description,
                Category: categories.find((cat) => cat.ID === categoryId),
                Observation: observation,
                IsMandatory: isMandatory,
                Value: value,
                DebitDate: debitDate,
              }
            : item
        )
      );
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao atualizar o lancamento.'));
      setErrorOpen(true);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseError = () => setErrorOpen(false);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(sortedData.map((item) => item.ID));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const calculateTotal = () => {
    const itemsToSum = selectedIds.length > 0
      ? data.filter((item) => selectedIds.includes(item.ID))
      : data;
    return itemsToSum.reduce((sum, item) => sum + Number(item.Value || 0), 0);
  };

  const handleItemCorrelationUpdate = (itemId, correlationFlags) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.ID === itemId
          ? { ...item, ...correlationFlags }
          : item
      )
    );
  };

  const handleItemLinkUpdate = (itemId, recurringId) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.ID === itemId ? { ...item, RecurringExpenseId: recurringId } : item
      )
    );
  };

  const hasActiveFilters =
    filters.category || filters.currency || filters.mandatory !== '' || filters.startDate || filters.endDate || filters.description || filters.showCorrelated;

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Despesas</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="outlined" size="small" onClick={() => setSuggestOpen(true)} sx={{ textTransform: 'none' }}>
            Sugerir vínculos
          </Button>
          <DialogButton buttonText="Nova Despesa" size="small">
            <CashOutForm onCreate={() => fetchData()} />
          </DialogButton>
        </Box>
      </Box>

      <SuggestLinksDialog
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        onApplied={() => fetchData()}
      />

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar..."
          value={filters.description}
          onChange={(e) => handleFilterChange('description', e.target.value)}
          size="small"
          sx={{ width: 200 }}
          InputProps={{
            endAdornment: filters.description && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleFilterChange('description', '')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Autocomplete
          options={categories}
          getOptionLabel={(option) => option.Name || ''}
          value={filters.category}
          onChange={(e, newValue) => handleFilterChange('category', newValue)}
          isOptionEqualToValue={(option, value) => option.ID === value?.ID}
          size="small"
          sx={{ width: 200 }}
          renderInput={(params) => <TextField {...params} placeholder="Categoria" />}
        />
        <Autocomplete
          options={currencies}
          getOptionLabel={(option) => option}
          value={filters.currency}
          onChange={(e, newValue) => handleFilterChange('currency', newValue)}
          size="small"
          sx={{ width: 150 }}
          renderInput={(params) => <TextField {...params} placeholder="Moeda" />}
        />
        <TextField
          select
          value={filters.mandatory}
          onChange={(e) => handleFilterChange('mandatory', e.target.value)}
          size="small"
          sx={{ width: 100 }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">Fixo?</MenuItem>
          <MenuItem value="true">Sim</MenuItem>
          <MenuItem value="false">Nao</MenuItem>
        </TextField>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <DatePicker
            label="Data início"
            value={filters.startDate ? dayjs(filters.startDate) : null}
            onChange={(newValue) => handleFilterChange('startDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
            format="DD/MM/YYYY"
            slotProps={{
              textField: { size: 'small', sx: { width: 200 } },
              field: { clearable: true },
            }}
          />
          <DatePicker
            label="Data fim"
            value={filters.endDate ? dayjs(filters.endDate) : null}
            onChange={(newValue) => handleFilterChange('endDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
            format="DD/MM/YYYY"
            slotProps={{
              textField: { size: 'small', sx: { width: 200 } },
              field: { clearable: true },
            }}
          />
        </LocalizationProvider>
        <Tooltip title="Mostrar despesas que já estão vinculadas a outras" arrow>
          <FormControlLabel
            control={
              <Switch
                checked={filters.showCorrelated}
                onChange={(e) => handleFilterChange('showCorrelated', e.target.checked)}
                size="small"
              />
            }
            label="Vinculadas"
            sx={{ ml: 1 }}
          />
        </Tooltip>
        {hasActiveFilters && (
          <Button variant="text" size="small" onClick={handleClearAllFilters} sx={{ textTransform: 'none' }}>
            Limpar
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      fontWeight: 500,
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      width: column.width,
                      display: column.hideOnMobile
                        ? { xs: 'none', md: 'table-cell' }
                        : column.hideOnSm
                        ? { xs: 'none', sm: 'table-cell' }
                        : column.hideOnLg
                        ? { xs: 'none', lg: 'table-cell' }
                        : 'table-cell',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                    sortDirection={orderBy === column.id ? order : false}
                    padding={column.id === 'select' ? 'checkbox' : 'normal'}
                  >
                    {column.id === 'select' ? (
                      <Checkbox
                        indeterminate={selectedIds.length > 0 && selectedIds.length < sortedData.length}
                        checked={sortedData.length > 0 && selectedIds.length === sortedData.length}
                        onChange={handleSelectAll}
                        size="small"
                      />
                    ) : column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={(e) => handleRequestSort(e, column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length > 0 ? (
                sortedData.map((item) => (
                  <EditableRow
                    key={item.ID}
                    item={item}
                    categories={categories}
                    onUpdate={handleUpdate}
                    onDelete={handleDelete}
                    onError={(msg) => {
                      setError(msg);
                      setErrorOpen(true);
                    }}
                    selected={selectedIds.includes(item.ID)}
                    onSelect={() => handleSelectOne(item.ID)}
                    onCorrelationUpdate={handleItemCorrelationUpdate}
                    recurringItems={recurringItems}
                    onLinkUpdate={handleItemLinkUpdate}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                      Nenhum lancamento encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {sortedData.length > 0 && (
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell colSpan={3} sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.875rem' }}>
                    {selectedIds.length > 0 ? `${selectedIds.length} selecionados` : `${sortedData.length} itens`}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'error.main' }}>
                    {formatValue(calculateTotal(), 'BRL')}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }} />
                  <TableCell />
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Por pagina:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </TableContainer>
      )}

      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CashOutList;
